import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from '@mui/material'
import { useHistoryCustomBack, usePanelHistoryTop } from 'weblite-router'
import { useShowAudioPlayer } from '@weblite/audio'
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from 'swiper/react'
import { RoomTypes } from '@weblite/ts-action/room'
import {
  APP_BAR_HEIGHT_CSS_VAR,
  CHAT_LIST_APP_BAR_HEIGHT,
} from '$common/app-bar/app-bar.constants'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { FullPageListSkeleton } from '$common/full-page-list-skeleton'
import { useMultiSelectList } from '$common/multi-select-searchable-list-page'
import {
  RoomListItem,
  useRoomListItems,
} from '$logic/room-list/room-list.react-hook'
import { NAVIGATION_BAR_HEIGHT } from '$common/navigation-bar'
import {
  exitRoomListFromSelectionMode,
  setRoomListToSelectionMode,
} from '$logic/router'
import { AudioPlayer } from '$common/audio-player'
import { AUDIO_PLAYER_HEIGHT } from '$common/audio-player/audio-player-constants'
import { CallBar } from '$comp/_app/components/chat/components/call-bar'
import { TopBarsContainer } from '$common/top-bars-container'
import { useShowCallBar } from '$logic/call'
import { vibrate } from '$func/vibrate'
import { UnseenChip } from '$common/chip'
import { useRoomListUnseenCount } from '$logic/room-seen/react-hook'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { ITab, Tabs } from '$common/tabs/tabs'
import { Ads } from '$comp/_app/components/leftPanel/components/chat-list/components/ad/ads'
import { useVisitedFolders } from './react-hooks'
import { EmptyChatList } from './components/empty-chat-list'
import { CreateRoomFab } from './components/create-room-fab'
import { ChatAppBar, ChatAppBarSelection } from './components/chat-app-bar'
import {
  STORY_RIBBON_HEIGHT_CSS_VAR,
  STORY_RIBBON_TRANSITION_MS,
} from '../../../story/constants'
import useTopOverscroll from '$hooks/scroll/use-top-overscroll'
import { StoryRibbonsApi } from '$comp/_app/components/story/story-ribbons-api'
import { StoryRibbons } from '$comp/_app/components/story/story-ribbons'
import {
  IS_STORY_ENABLED,
  SHOULD_CATEGORIZE_CHATS,
} from '$func/environmentInfo'
import { useStateRef } from '$hooks/use-state-ref'
import { useWorkspacesIds, DEFAULT_WORKSPACE_ID } from '$logic/workspace'
import { useCurrentWorkSpaceId } from '$comp/main/react-hook'
import { IS_IN_ALPHA_SCHOOL, WORKSPACE_ENABLED } from '$config/env'
import { getAppbarHeight } from '$comp/_app/components/leftPanel/components/chat-list/helper'
import { ListRenderer } from '$comp/_app/components/leftPanel/components/chat-list/list-renderer'
import { AppbarApi } from '$comp/_app/components/leftPanel/components/chat-list/components/chat-app-bar/chat-app-bar-api'
import { dispatchSetWorkspace } from '$comp/main/root.action'
import { Scroller } from '$common/scroller'
import { CALL_BAR_HEIGHT } from '$comp/_app/components/chat/components/conf'
import { PromoteToPremium } from './components/promote-to-premium'
import { WorkspaceAppBar } from './components/chat-app-bar/workspace-appbar'
import { CreateCourseOnboardingBar } from './components/create-course-onboarding-bar/create-course-onboarding-bar'

export const TABS_HEIGHT = SHOULD_CATEGORIZE_CHATS ? 39 : 0
export const CHAT_LIST_WRAPPER_ID = 'chatListWrapper'

function a11yProps(index: number) {
  return {
    key: `chat-list-tab-${index}`,
    'aria-controls': `chat-list-tab-panel-${index}`,
  }
}

export type ChatSelectionTabTypes = 'ALL' | RoomTypes

export const folderChatsTypes = [
  'ALL',
  'PV',
  'CHANNEL',
  'GROUP',
] as ChatSelectionTabTypes[]

export function ChatList() {
  const { items, loading, workspacesChatIds } = useRoomListItems()
  const theme = useTheme()

  const containerRef = useRef<HTMLDivElement>(null)

  const currentWorkSpaceId = useCurrentWorkSpaceId()

  const isCurrentWorkSpaceDefault =
    currentWorkSpaceId === DEFAULT_WORKSPACE_ID || !WORKSPACE_ENABLED

  const chatListStoryRibbonHeight = isCurrentWorkSpaceDefault
    ? STORY_RIBBON_HEIGHT_CSS_VAR
    : '0px'

  const [triggerIsDisabled, setTriggerIsDisabled] = useState(false)

  const isDefaultWorkspace = currentWorkSpaceId === DEFAULT_WORKSPACE_ID

  const {
    selectionMap,
    setSelection,
    resetSelection,
    selectedItems,
    hasSelection,
  } = useMultiSelectList<RoomListItem>({
    items: isDefaultWorkspace
      ? items
      : workspacesChatIds[currentWorkSpaceId] || [],
    selectionPrimaryKey: 'chatId',
  })

  const handleShowStoryRibbon = useCallback(() => {
    StoryRibbonsApi.toggleStoryRibbons({ isShown: true })
  }, [])

  const handleHideStoryRibbon = useCallback(() => {
    StoryRibbonsApi.toggleStoryRibbons({ isShown: false })
  }, [])

  const overflowHappenedCallback = () => {
    if (IS_STORY_ENABLED) handleShowStoryRibbon()
    if (WORKSPACE_ENABLED) {
      const newHeight = getAppbarHeight(true, hasSelection, currentWorkSpaceId)
      AppbarApi.setAppbarHeight({ height: newHeight, expanded: true })
    }
  }

  const overflowResetCallback = useCallback(() => {
    handleHideStoryRibbon()
    const newHeight = getAppbarHeight(false, hasSelection, currentWorkSpaceId)
    AppbarApi.setAppbarHeight({ height: newHeight, expanded: false })
  }, [handleHideStoryRibbon, currentWorkSpaceId, hasSelection])

  const renderedOverflowTrigger = useTopOverscroll(
    containerRef,
    overflowHappenedCallback,
    overflowResetCallback,
    (!IS_STORY_ENABLED && !WORKSPACE_ENABLED) || triggerIsDisabled,
  )

  const sliderRef = useRef<SwiperRef>(null)
  const { showAudioPlayer } = useShowAudioPlayer()

  const [currentChatsType, setCurrentChatsType] =
    useState<ChatSelectionTabTypes>('ALL')

  const panelMode = usePanelHistoryTop('roomList')?.state?.mode

  const panelModeRef = useStateRef(panelMode)

  const homePanelState = usePanelHistoryTop('home')?.state
  const isTop = homePanelState?.tab === 'roomList'

  useHistoryCustomBack(
    () => {
      if (hasSelection) exitRoomListFromSelectionMode()
      else return 'closeApp'
    },
    [hasSelection],
    isTop,
  )

  useEffect(() => {
    if (hasSelection) StoryRibbonsApi.toggleStoryRibbons({ isShown: false })
  }, [hasSelection])

  // NOTE: Change panel mode when user select or deselect items
  useEffect(() => {
    if (panelModeRef.current === 'SELECT' && !selectedItems.length)
      exitRoomListFromSelectionMode()
    if (panelModeRef.current !== 'SELECT' && selectedItems.length)
      setRoomListToSelectionMode()
  }, [selectedItems])

  // NOTE: Deselect items when user clicked back button
  useEffect(() => {
    if (panelMode !== 'SELECT') {
      resetSelection()
    }
  }, [panelMode, resetSelection])

  const onItemSelectedChange = useCallback(
    (selected: boolean, chatId: string) => {
      setSelection(chatId, selected)
      if (selected) vibrate()
    },
    [setSelection],
  )

  const selectionCount = useMemo(
    () => Object.values(selectionMap).filter(Boolean).length,
    [selectionMap],
  )

  const handleChatTypeChange = useCallback(
    (newChatsType: ChatSelectionTabTypes) => {
      const chatListWrapper = document.getElementById(CHAT_LIST_WRAPPER_ID)
      if (chatListWrapper && chatListWrapper.scrollTop > 1)
        chatListWrapper.scrollTo({
          // to prevent top over scroll
          top: 1,
          left: 0,
          behavior: 'smooth',
        })

      sliderRef.current?.swiper.slideTo(
        folderChatsTypes.findIndex(type => type === newChatsType),
      )
      setCurrentChatsType(newChatsType)
    },
    [currentChatsType],
  )

  const rawWorkspaceIds = useWorkspacesIds()
  const workspaceIds = [DEFAULT_WORKSPACE_ID, ...rawWorkspaceIds]

  const savedScrollRef = useRef<number | null>(null)

  useEffect(() => {
    const savedScroll = savedScrollRef.current
    const scrollableNode = document.getElementById(CHAT_LIST_WRAPPER_ID)

    if (currentWorkSpaceId === 'common' && savedScroll && scrollableNode) {
      swiperRef.current?.on('slideChangeTransitionEnd', () => {
        setTimeout(() => {
          scrollableNode.scrollTo({
            // to prevent top over scroll
            top: savedScroll,
            left: 0,
            behavior: 'smooth',
          })
        }, 100)
      })
    }
    return () => {
      if (currentWorkSpaceId === 'common') {
        if (scrollableNode) savedScrollRef.current = scrollableNode.scrollTop
      }
    }
  }, [currentWorkSpaceId])

  useEffect(() => {
    setTriggerIsDisabled(true)
    AppbarApi.setAppbarHeight({
      height: getAppbarHeight(true, hasSelection, currentWorkSpaceId),
      expanded: true,
    })

    const workspaceIndex = workspaceIds.findIndex(
      id => id === currentWorkSpaceId,
    )
    sliderRef.current?.swiper.slideTo(workspaceIndex)
    setTimeout(() => {
      setTriggerIsDisabled(false)
    }, 300)
  }, [currentWorkSpaceId])

  const unseenCount = useRoomListUnseenCount()

  const tabs = useMemo<ITab<ChatSelectionTabTypes>[]>(() => {
    return folderChatsTypes.map((chatType, index) => ({
      label: t(`chatList.tabs.${chatType.toLowerCase()}`),
      icon: unseenCount[chatType] ? (
        <UnseenChip
          color={chatType === currentChatsType ? 'neutral800' : 'neutral000'}
          label={
            unseenCount[chatType] > 99
              ? `+${toLocale(99)}`
              : toLocale(unseenCount[chatType])
          }
          sx={{
            borderColor: 'neutral300',
            borderWidth: chatType === currentChatsType ? '0' : '1px',
            borderStyle: 'solid',
          }}
        />
      ) : undefined,
      iconPosition: 'end',
      value: chatType,
      ...a11yProps(index),
    }))
  }, [unseenCount, currentChatsType])

  const showCallBar = useShowCallBar()

  const { visitedFoldersMap, visitFolder } = useVisitedFolders()

  useEffect(() => {
    // NOTE: need to disable because appbar disappears and appears
    if (hasSelection) setChatListTransitionCSSVar('none')
    else
      setTimeout(() => {
        setChatListTransitionCSSVar(`height ${STORY_RIBBON_TRANSITION_MS}ms`)
      }, STORY_RIBBON_TRANSITION_MS + 50)
  }, [hasSelection])

  const audioPlayerHeight = showAudioPlayer ? AUDIO_PLAYER_HEIGHT : 0
  const callBarHeight = showCallBar ? CALL_BAR_HEIGHT : 0
  const tabsHeight = SHOULD_CATEGORIZE_CHATS ? TABS_HEIGHT : 0

  // const [showItems, setShowItems] = useState(true)
  // useEffect(() => {
  //   console.timeEnd('toggle')
  // }, [showItems])
  //
  // useEffect(() => {
  //   const handler = (e: any) => {
  //     if (e.code === 'KeyS') {
  //       console.time('toggle')
  //       setShowItems(show => !show)
  //       setCurrentChatsType('ALL')
  //     }
  //   }
  //
  //   window.addEventListener('keydown', handler)
  //   return () => {
  //     window.removeEventListener('keydown', handler)
  //   }
  // }, [])

  const swiperRef = useRef<SwiperClass>(null)

  const updateSwiperHeight = useCallback(() => {
    swiperRef.current?.updateAutoHeight()
  }, [])

  return (
    <PageWithAppBar
      bodySx={{
        position: 'relative',
        height: `calc(100% - ${chatListStoryRibbonHeight} - ${hasSelection ? `${CHAT_LIST_APP_BAR_HEIGHT}px` : APP_BAR_HEIGHT_CSS_VAR})`,
        flexGrow: 0,
        overflow: 'hidden',
        isolation: 'isolate',
        transition: 'var(--chat-list-transition)',
      }}
      appBar={
        <>
          {hasSelection ? (
            <ChatAppBarSelection selectedItems={selectedItems} />
          ) : isCurrentWorkSpaceDefault ? (
            <ChatAppBar />
          ) : (
            <WorkspaceAppBar />
          )}
          {IS_STORY_ENABLED && isCurrentWorkSpaceDefault && <StoryRibbons />}
        </>
      }
    >
      {loading ? (
        <FullPageListSkeleton size="large" />
      ) : !items.length ? (
        <EmptyChatList />
      ) : (
        <>
          {SHOULD_CATEGORIZE_CHATS && (
            <Tabs
              tabs={tabs}
              tabsSx={{ height: TABS_HEIGHT, direction: 'ltr', flexShrink: 0 }}
              tabSx={{
                color: theme.palette.neutral900,
                '&.MuiTab-root': {
                  opacity: 0.7,
                },
                '&[data-selected="true"]': {
                  opacity: 1,
                },
              }}
              value={currentChatsType}
              onChange={handleChatTypeChange}
              isLtr
            />
          )}

          <TopBarsContainer
            isStatic
            hasAnyChild={showAudioPlayer || showCallBar}
          >
            {showCallBar && <CallBar />}
            {showAudioPlayer && <AudioPlayer />}
          </TopBarsContainer>
          <Scroller
            ref={containerRef}
            scrollableNodeChildrenSlot={renderedOverflowTrigger}
            // element="div"
            scrollableNodeProps={{
              id: CHAT_LIST_WRAPPER_ID,
            }}
            style={{
              height: `calc(100% - ${
                callBarHeight + tabsHeight + audioPlayerHeight
              }px)`,
              backgroundColor: theme.palette.neutral000,
            }}
          >
            {SHOULD_CATEGORIZE_CHATS ? (
              <Swiper
                ref={sliderRef}
                onInit={swiper => (swiperRef.current = swiper)}
                autoHeight
                dir="ltr"
                touchStartPreventDefault={false}
                onSlideChange={e => {
                  const folder = folderChatsTypes[e.realIndex]
                  setCurrentChatsType(folder)
                  visitFolder(folder)
                }}
              >
                {folderChatsTypes.map(chatType => (
                  <SwiperSlide
                    key={chatType}
                    dir="rtl"
                    style={{
                      minHeight: `calc(100% - ${
                        callBarHeight + tabsHeight + audioPlayerHeight
                      }px - ${STORY_RIBBON_HEIGHT_CSS_VAR} - ${APP_BAR_HEIGHT_CSS_VAR} )`,
                    }}
                  >
                    <>
                      <Ads />
                      <ListRenderer
                        selectionCount={selectionCount}
                        selectionMap={selectionMap}
                        items={items}
                        onItemSelectedChange={onItemSelectedChange}
                        chatType={chatType}
                        visited={visitedFoldersMap[chatType]}
                        onUpdateHeight={updateSwiperHeight}
                      />
                    </>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : WORKSPACE_ENABLED && workspaceIds?.length ? (
              <Swiper
                ref={sliderRef}
                autoHeight
                // dir="ltr"
                touchStartPreventDefault={false}
                onSlideChange={e => {
                  dispatchSetWorkspace(workspaceIds[e.realIndex])
                }}
                onInit={swiper => (swiperRef.current = swiper)}
              >
                {workspaceIds.map(workspaceId => (
                  <SwiperSlide
                    key={workspaceId}
                    // dir="rtl"
                    style={{
                      minHeight: `calc(100% - ${
                        callBarHeight + tabsHeight + audioPlayerHeight
                      }px - ${chatListStoryRibbonHeight} - ${APP_BAR_HEIGHT_CSS_VAR} )`,
                    }}
                  >
                    {(workspaceId === DEFAULT_WORKSPACE_ID ||
                      !WORKSPACE_ENABLED) &&
                      !IS_IN_ALPHA_SCHOOL && <PromoteToPremium />}
                    <ListRenderer
                      selectionCount={selectionCount}
                      selectionMap={selectionMap}
                      items={
                        workspaceId === DEFAULT_WORKSPACE_ID
                          ? items
                          : workspacesChatIds[workspaceId]
                      }
                      onItemSelectedChange={onItemSelectedChange}
                      workspaceId={workspaceId}
                      visited={true}
                      onUpdateHeight={updateSwiperHeight}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <>
                <Ads />
                <ListRenderer
                  selectionCount={selectionCount}
                  selectionMap={selectionMap}
                  items={items}
                  onItemSelectedChange={onItemSelectedChange}
                  chatType="ALL"
                  visited={visitedFoldersMap.ALL}
                />
              </>
            )}
          </Scroller>
          {/* <ToolboxFab /> */}

          {/* <CreateRoomFab /> */}
        </>
      )}
    </PageWithAppBar>
  )
}

export function setChatListTransitionCSSVar(transition: string) {
  document.documentElement?.style.setProperty(
    '--chat-list-transition',
    transition,
  )
}
